import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import BackToTop from "../Components/BackToTop";
import QuotationForm from "../Components/QuotationForm";
import { downloadProductPamphlet } from "../utils/catalogueDownload";
import { apiUrl, getAssetUrl } from "../utils/api";
import { trackAnalyticsEvent } from "../utils/analytics";
import "../styles/Products.css";

const parseSizes = (sizes) => {
  if (!sizes) return [];
  if (Array.isArray(sizes)) return sizes;
  if (typeof sizes === "string") {
    const trimmed = sizes.trim();
    if (trimmed.startsWith("[")) {
      try { return JSON.parse(trimmed); } catch (e) { }
    }
    return trimmed.split(",").map((s) => s.trim()).filter(Boolean);
  }
  return [];
};

const getSizeUnitLabel = (sizeUnit) => {
  switch (sizeUnit) {
    case "mm":
      return "in mm";
    case "number":
      return "in NO";
    default:
      return "in inches";
  }
};

const getDailySeed = () => {
  const today = new Date();
  const utcDate = Date.UTC(
    today.getUTCFullYear(),
    today.getUTCMonth(),
    today.getUTCDate()
  );

  return Math.floor(utcDate / 86400000);
};

const seededRandom = (seed) => {
  const nextSeed = (seed * 1664525 + 1013904223) % 4294967296;
  return {
    seed: nextSeed,
    value: nextSeed / 4294967296,
  };
};

const getDailyProductOrder = (products, seedOffset = 0) => {
  const ordered = [...products];
  let seed = getDailySeed() + seedOffset;

  for (let i = ordered.length - 1; i > 0; i -= 1) {
    const next = seededRandom(seed);
    seed = next.seed;
    const j = Math.floor(next.value * (i + 1));
    [ordered[i], ordered[j]] = [ordered[j], ordered[i]];
  }

  return ordered;
};

const getStringSeed = (value) =>
  String(value || "")
    .split("")
    .reduce((seed, char) => seed + char.charCodeAt(0), 0);

const Products = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const queryParams = new URLSearchParams(location.search);
  const defaultCategory = queryParams.get("category") || "all";

  const [productsData, setProductsData] = useState({});
  const [orderedProducts, setOrderedProducts] = useState([]);
  const [activeCategory, setActiveCategory] = useState(defaultCategory);
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showQuoteForm, setShowQuoteForm] = useState(false);

  useEffect(() => {
    const controller = new AbortController();

    async function loadProducts() {
      try {
        const response = await fetch(apiUrl("/products"), {
          signal: controller.signal,
          headers: { Accept: "application/json" },
        });
        if (!response.ok) throw new Error("Failed to load products");
        const { products } = await response.json();
        const dailyProducts = getDailyProductOrder(products || []);
        const grouped = dailyProducts.reduce((acc, product) => {
          const category = product.category?.trim() || "Uncategorized";
          if (!acc[category]) acc[category] = [];
          acc[category].push(product);
          return acc;
        }, {});

        Object.keys(grouped).forEach((category) => {
          grouped[category] = getDailyProductOrder(
            grouped[category],
            getStringSeed(category)
          );
        });

        setProductsData(grouped);
        setOrderedProducts(dailyProducts);
      } catch (err) {
        if (err.name !== "AbortError") console.error(err);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    }
    loadProducts();

    return () => controller.abort();
  }, []);

  useEffect(() => {
    const param = new URLSearchParams(location.search).get("category");
    setActiveCategory(param || "all");
  }, [location.search]);

  useEffect(() => {
    if (loading || activeCategory === "all") return;

    trackAnalyticsEvent("category_view", {
      category: activeCategory,
    });
  }, [activeCategory, loading]);

  const handleImageError = (e) => {
    if (!e.target.src.includes("no-image.png")) {
      e.target.src = "/images/no-image.png";
    }
  };

  const openDetails = (product, e) => {
    // prevent bubbling from buttons inside the card
    if (e && e.target.closest("button")) return;
    trackAnalyticsEvent("product_view", {
      productId: product.id,
      productName: product.name,
      category: product.category,
    });
    setSelectedProduct(product);
    setShowQuoteForm(false);
  };

  const categories = Object.keys(productsData);

  const filteredProducts =
    activeCategory === "all"
      ? orderedProducts
      : productsData[activeCategory] || [];

  const handleCatalogueDownload = () => {
    navigate("/products/catalogue");
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="products-page">
      {/* CATEGORY TABS */}
      <section className="category-tabs">
        <div className="tabs-container">
          <button
            className={`category-tab ${activeCategory === "all" ? "active" : ""}`}
            onClick={() => navigate("/products?category=all")}
          >
            All
          </button>
          {categories.map((cat) => (
            <button
              key={cat}
              className={`category-tab ${activeCategory === cat ? "active" : ""}`}
              onClick={() => navigate(`/products?category=${cat}`)}
            >
              {cat}
            </button>
          ))}
        </div>
      </section>

      {/* PRODUCTS */}
      <section className="product-category">
        <div className="container">
          <h1 className="category-title">
            {activeCategory === "all" ? "All Products" : activeCategory}
          </h1>
          <div className="product-header">
            <button
              type="button"
              className="catalogue-download-btn"
              onClick={handleCatalogueDownload}
            >
              View Catalogue
            </button>
            <p className="product-note-warning">
              Note: Packing and rates may vary. Please request a quotation.
            </p>
          </div>

          <div className="product-grid">
            {filteredProducts.map((product) => (
              <div
                key={product.id}
                className="product-card"
                onClick={(e) => openDetails(product, e)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === "Enter" && openDetails(product)}
                aria-label={`View details for ${product.name}`}
              >
                <div className="product-image">
                  <img
                    src={getAssetUrl(product.image)}
                    alt={product.name}
                    onError={handleImageError}
                    loading="lazy"
                    decoding="async"
                    sizes="(max-width: 480px) 50vw, (max-width: 900px) 33vw, 240px"
                  />
                  {/* hover overlay hint */}
                  <div className="product-image-overlay">
                    <span>View Details</span>
                  </div>
                </div>

                <div className="product-info">
                  <h3>{product.name}</h3>

                  <div className="product-actions">
                    <button
                      className="view-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        trackAnalyticsEvent("product_view", {
                          productId: product.id,
                          productName: product.name,
                          category: product.category,
                        });
                        setSelectedProduct(product);
                        setShowQuoteForm(false);
                      }}
                    >
                      View Details
                    </button>

                    <button
                      className="quote-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedProduct(product);
                        setShowQuoteForm(true);
                      }}
                    >
                      Get Quotation
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DETAILS MODAL */}
      {selectedProduct && !showQuoteForm && (
        <div className="modal-overlay" onClick={() => setSelectedProduct(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>

            {/* Left — image */}
            <div className="img-col">
              <img src={getAssetUrl(selectedProduct.image)} alt={selectedProduct.name} />
            </div>

            {/* Right — info */}
            <div className="info-col">
              <button className="close-btn" onClick={() => setSelectedProduct(null)}>&times;</button>

              <h2 className="product-name">{selectedProduct.name}</h2>
              <div className="divider" />

              <p className="desc">
                {selectedProduct.description || "No description available."}
              </p>

              {selectedProduct.sizes && parseSizes(selectedProduct.sizes).length > 0 && (
                <>
                  <p className="spec-label">
                    Available Sizes ({getSizeUnitLabel(selectedProduct.sizeUnit)})
                  </p>
                  <div className="sizes">
                    {parseSizes(selectedProduct.sizes).map((size, i) => (
                      <span key={i} className="size-tag">{size}</span>
                    ))}
                  </div>
                </>
              )}

              {selectedProduct.material && (
                <>
                  <p className="spec-label">Material</p>
                  <p className="material">{selectedProduct.material}</p>
                </>
              )}

              <div className="cta">
                <button className="btn-primary" onClick={() => setShowQuoteForm(true)}>
                  Request a Quote
                </button>
                <button
                  className="btn-secondary"
                  onClick={() => downloadProductPamphlet(selectedProduct)}
                >
                  Download Pamphlet
                </button>
              </div>
            </div>

          </div>
        </div>
      )}

      {/* QUOTATION FORM */}
      {showQuoteForm && selectedProduct && (
        <QuotationForm
          productName={selectedProduct.name}
          productImage={getAssetUrl(selectedProduct.image)}
          onClose={() => {
            setShowQuoteForm(false);
            setSelectedProduct(null);
          }}
        />
      )}

      <BackToTop />
    </div>
  );
};

export default Products;
